/**
 * Platform Detection & Display Strings
 *
 * Handles platform-specific shortcut display and modifier resolution.
 */

import type { Platform, Browser, ParsedShortcut, PlatformShortcut } from './types';

// ============================================================================
// DISPLAY SYMBOLS
// ============================================================================

const MAC_SYMBOLS: Record<string, string> = {
	mod: '⌘',
	cmd: '⌘',
	meta: '⌘',
	ctrl: '⌃',
	alt: '⌥',
	option: '⌥',
	shift: '⇧',
	enter: '↵',
	return: '↵',
	escape: '⎋',
	esc: '⎋',
	delete: '⌫',
	backspace: '⌫',
	tab: '⇥',
	space: '␣',
	up: '↑',
	down: '↓',
	left: '←',
	right: '→',
};

const WIN_LINUX_DISPLAY: Record<string, string> = {
	mod: 'Ctrl',
	ctrl: 'Ctrl',
	alt: 'Alt',
	shift: 'Shift',
	meta: 'Win',
	enter: 'Enter',
	return: 'Enter',
	escape: 'Esc',
	esc: 'Esc',
	delete: 'Del',
	backspace: 'Backspace',
	tab: 'Tab',
	space: 'Space',
	up: '↑',
	down: '↓',
	left: '←',
	right: '→',
};

// ============================================================================
// DISPLAY STRING GENERATION
// ============================================================================

/**
 * Convert a parsed shortcut to a display string for the given platform.
 */
export function shortcutToDisplay(shortcut: string, platform: Platform): string {
	const parts = shortcut.split(' ').map((part) => partToDisplay(part, platform));
	return parts.join(' ');
}

function partToDisplay(part: string, platform: Platform): string {
	const pieces = part.toLowerCase().split('+');
	const symbols = platform === 'mac' ? MAC_SYMBOLS : WIN_LINUX_DISPLAY;

	const result: string[] = [];
	for (const piece of pieces) {
		if (symbols[piece]) {
			result.push(symbols[piece]);
		} else {
			// Capitalize single letters
			result.push(piece.length === 1 ? piece.toUpperCase() : capitalize(piece));
		}
	}

	if (platform === 'mac') {
		// Mac style: ⌘⇧C (no separator)
		return result.join('');
	} else {
		// Windows/Linux style: Ctrl+Shift+C
		return result.join('+');
	}
}

function capitalize(s: string): string {
	return s.charAt(0).toUpperCase() + s.slice(1);
}

// ============================================================================
// PLATFORM SHORTCUT RESOLUTION
// ============================================================================

/**
 * Resolve a platform-specific shortcut specification to the actual shortcut
 * string for the current platform.
 */
export function resolvePlatformShortcut(
	spec: string | PlatformShortcut,
	platform: Platform
): string | null {
	if (typeof spec === 'string') {
		return spec;
	}

	// Try platform-specific first
	if (platform === 'mac' && spec.mac) {
		return spec.mac;
	}
	if (platform === 'windows' && spec.windows) {
		return spec.windows;
	}
	if (platform === 'linux' && spec.linux) {
		return spec.linux;
	}

	// Fall back to default
	if (spec.default) {
		return spec.default;
	}

	return null;
}

// ============================================================================
// MODIFIER KEY MAPPING
// ============================================================================

/**
 * Get the actual modifier key for 'mod' on a given platform.
 * On Mac, mod = meta (Cmd). On Windows/Linux, mod = ctrl.
 */
export function getModKey(platform: Platform): 'meta' | 'ctrl' {
	return platform === 'mac' ? 'meta' : 'ctrl';
}

/**
 * Check if a keyboard event's modifiers match the parsed shortcut requirements.
 *
 * If no modifiers are specified in the shortcut, it matches regardless of
 * what modifiers are held. If any modifier is specified, exact match is required.
 */
export function modifiersMatch(
	eventMods: { ctrl: boolean; alt: boolean; shift: boolean; meta: boolean },
	required: ParsedShortcut['modifiers'],
	platform: Platform,
	/** The key being pressed - used to exclude self-modifier (e.g., 'alt' key sets alt modifier) */
	pressedKey?: string
): boolean {
	// Handle 'mod' - maps to meta on mac, ctrl on win/linux
	const modKey = getModKey(platform);
	const requiredMod = required.mod;

	// Check if any modifier is required
	const ctrlRequired = required.ctrl || (requiredMod && modKey === 'ctrl');
	const metaRequired = required.meta || (requiredMod && modKey === 'meta');
	const hasAnyRequired = ctrlRequired || metaRequired || required.alt || required.shift;

	// If no modifiers specified, require no command-related modifiers
	// (shift is allowed since it's used for uppercase letters)
	// Exception: if the pressed key IS a modifier key, allow other modifiers to be held
	// (e.g., releasing 'shift' while holding 'alt' should still match 'shift' shortcut)
	if (!hasAnyRequired) {
		const keyLower = pressedKey?.toLowerCase();
		const isModifierKey =
			keyLower === 'ctrl' ||
			keyLower === 'control' ||
			keyLower === 'alt' ||
			keyLower === 'option' ||
			keyLower === 'meta' ||
			keyLower === 'cmd' ||
			keyLower === 'command' ||
			keyLower === 'shift';

		// If the key is a modifier key itself, allow any combination of modifiers
		// (needed for keyup events where other modifiers may still be held)
		if (isModifierKey) {
			return true;
		}

		// For regular keys, fail if ctrl, alt, or meta are pressed when none required
		if (eventMods.ctrl || eventMods.alt || eventMods.meta) {
			return false;
		}
		return true;
	}

	// Otherwise require exact match
	if (eventMods.ctrl !== ctrlRequired) return false;
	if (eventMods.alt !== required.alt) return false;
	if (eventMods.shift !== required.shift) return false;
	if (eventMods.meta !== metaRequired) return false;

	return true;
}
